import { FunctionComponent, useEffect, useState, useRef, useContext } from 'react';
import { ButtonPrimary, FormGroup, FormHelpText, FormInput } from '@spotify-internal/encore-web';
import { useTranslation } from 'react-i18next';
import { isEmpty, isUndefined, mapValues, mergeWith, size, some } from 'lodash';

import analytics, {
  ShowStepEventLabel,
} from 'components/ContentReporting/ContentLegalReporting/report-content-analytics-utils';
import { FormData } from 'containers/ContentReporting/ContentLegalReporting/utils/types';
import {
  YourDetailsInputs,
  YourDetailsInputsErrors,
} from 'containers/ContentReporting/ContentLegalReporting/utils/config/steps/reportedContent';
import { UserInfoContext } from 'context/UserInfo/UserInfoProvider';

import { CLAIMANT_DETAILS } from '../content-legal-reporting-config';

import { STEP_1_FIELDS } from './fields';

type Step1YourDetailsProps = {
  formData: FormData;
  onEmailFieldChange: (email: string) => void;
  renderErrorMessage: () => void;
  goToNextStep: (requiredFields: string[], newFormValues: FormData) => void;
};

export const getPayloadFields = () => STEP_1_FIELDS.map(field => field.fieldName);

export const Step1YourDetails: FunctionComponent<Step1YourDetailsProps> = props => {
  const { onEmailFieldChange, formData, goToNextStep, renderErrorMessage } = props;
  const { userAccountEmail } = useContext(UserInfoContext);

  const formDataToStep1Values = {
    [CLAIMANT_DETAILS.fields.CLAIMANT_FIRST_NAME]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_FIRST_NAME]?.value,
    [CLAIMANT_DETAILS.fields.CLAIMANT_LAST_NAME]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_LAST_NAME]?.value,
    [CLAIMANT_DETAILS.fields.CLAIMANT_COMPANY_NAME]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_COMPANY_NAME]?.value,
    [CLAIMANT_DETAILS.fields.CLAIMANT_ADDRESS]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_ADDRESS]?.value,
    [CLAIMANT_DETAILS.fields.CLAIMANT_PHONE]: (
      formData[CLAIMANT_DETAILS.fields.CLAIMANT_PHONE]?.value || ''
    ).replaceAll(
      // eslint-disable-next-line no-control-regex
      /\s|\u0000/g,
      '',
    ),
    [CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL]?.value,
    [CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO]: formData[CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO]?.value,
  };

  const defaultStep1Values = {
    [CLAIMANT_DETAILS.fields.CLAIMANT_FIRST_NAME]: '',
    [CLAIMANT_DETAILS.fields.CLAIMANT_LAST_NAME]: '',
    [CLAIMANT_DETAILS.fields.CLAIMANT_COMPANY_NAME]: undefined,
    [CLAIMANT_DETAILS.fields.CLAIMANT_ADDRESS]: undefined,
    [CLAIMANT_DETAILS.fields.CLAIMANT_PHONE]: undefined,
    [CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL]: '',
    [CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO]: undefined,
  };

  const defaultFormValues = mergeWith(
    {},
    formDataToStep1Values,
    defaultStep1Values,
    (formDataAttribute, defaultValue) => (isUndefined(formDataAttribute) ? defaultValue : formDataAttribute),
  ) as YourDetailsInputs;

  const [formValues, setFormValues] = useState<YourDetailsInputs>(defaultFormValues);
  const [inputErrors, setInputErrors] = useState<YourDetailsInputsErrors>({});
  const userAccountEmailRef = useRef(userAccountEmail);

  if (userAccountEmail && userAccountEmail !== userAccountEmailRef.current) {
    userAccountEmailRef.current = userAccountEmail;
    setFormValues({ ...formValues, [CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL]: userAccountEmail });
  }

  const { t } = useTranslation('infringement_claim_form');

  useEffect(() => {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step1ClaimantInformation);
    window.scrollTo(0, 0);
  }, []);

  const validate = (newInputs: YourDetailsInputs): YourDetailsInputsErrors => {
    return STEP_1_FIELDS.reduce((errors, field) => {
      const fieldRequiredError = field.required
        ? isEmpty(newInputs[field.fieldName as keyof YourDetailsInputs])
        : false;
      const fieldLengthError = size(newInputs[field.fieldName as keyof YourDetailsInputs]) > field.maxLength;
      return {
        ...errors,
        [field.fieldName]: fieldRequiredError || fieldLengthError,
      };
    }, {});
  };

  const validateFormFieldsAndSubmit = (currentFormValues: YourDetailsInputs) => {
    const formErrors = validate(currentFormValues);
    setInputErrors(formErrors);
    const hasFieldsErrors = some(formErrors, Boolean);

    if (!hasFieldsErrors) {
      goToNextStep(
        [],
        mapValues(currentFormValues, value => ({ value, valid: true })),
      );
    }
  };

  return (
    <>
      {STEP_1_FIELDS.map(
        ({ fieldName, fieldLabel, required, maxLength, type = 'text', errorMessageKey = 'field_required' }) => (
          <FormGroup
            label={t(fieldLabel)}
            labelFor={fieldLabel}
            maxLength={maxLength}
            indicator={required ? 'required' : undefined}
            key={fieldName}
          >
            <FormInput
              id={fieldLabel}
              type={type}
              defaultValue={formValues[fieldName as keyof YourDetailsInputs]}
              onBlur={event => {
                const value = event.target.value;
                setFormValues(currentFormValues => ({ ...currentFormValues, [fieldName]: value }));
                if (fieldName === CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL) {
                  onEmailFieldChange(value);
                }
              }}
              disabled={fieldName === CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL && !!userAccountEmail}
            />
            {!!inputErrors[fieldName as keyof YourDetailsInputs] && (
              <FormHelpText error id={fieldLabel}>
                {t(errorMessageKey)}
              </FormHelpText>
            )}
          </FormGroup>
        ),
      )}
      {renderErrorMessage()}
      <ButtonPrimary onClick={() => validateFormFieldsAndSubmit(formValues)}>{t('continue')}</ButtonPrimary>
    </>
  );
};

export default Step1YourDetails;
